import io from "socket.io-client";

import { config } from "@admin/config/config";

interface DepartureAssignmentUpdate {
	departureId: number;
	assignment?: any;
	departure?: any;
	type?: "CREATE" | "DELETE" | "UPDATE";
}

type DepartureUpdateHandler = (data: DepartureAssignmentUpdate) => void;
export type { DepartureAssignmentUpdate, DepartureUpdateHandler };
export type DeparturesRefreshHandler = (data: { type: "regular" | "road-scholar" }) => void;

class SocketService {
	private socket: ReturnType<typeof io> | null = null;
	private departureUpdateHandlers: Set<DepartureUpdateHandler> = new Set();
	private isInitialized = false;
	private static instance: SocketService | null = null;
	private reconnectAttempts = 0;
	private authError = false;
	private maxReconnectAttempts = 5;

	private constructor() {}

	static getInstance(): SocketService {
		if (!SocketService.instance) {
			SocketService.instance = new SocketService();
		}
		return SocketService.instance;
	}

	initialize() {
		if (this.isInitialized || this.socket) {
			console.log("Socket already initialized");
			return;
		}

		try {
			this.isInitialized = true;
			this.reconnectAttempts = 0;
			this.authError = false;

			const socketUrl = config.VITE_SOCKET_URL;
			console.log(
				socketUrl
					? `Initializing socket connection to ${socketUrl}`
					: "Initializing socket connection to same-origin"
			);

			// Force a refresh of auth before connecting socket
			// This ensures the access_token cookie is fresh
			this.refreshAuthBeforeConnect(socketUrl);
		} catch (error) {
			console.error("Error initializing socket:", error);
			this.isInitialized = false;
			this.socket = null;
		}
	}

	// New method to refresh auth before connecting socket
	private async refreshAuthBeforeConnect(socketUrl: string) {
		try {
			console.log("Preparing for socket connection with proper authentication");

			// First check authentication status with /auth/me
			// This is a lightweight way to verify if already authenticated
			const authCheckResponse = await fetch(`${config.VITE_API_BASE_URL}/auth/me`, {
				method: "GET",
				credentials: "include", // Include cookies
			});

			if (authCheckResponse.ok) {
				console.log("User already authenticated, proceeding with socket connection");

				// Connect immediately when already authenticated - no delay needed
				this.connectSocket(socketUrl);
				return;
			}

			// Not authenticated, refresh the token
			console.log("Auth refresh needed before socket connection");

			const refreshResponse = await fetch(`${config.VITE_API_BASE_URL}/auth/refresh`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
				},
			});

			if (refreshResponse.ok) {
				console.log("Auth refreshed successfully, connecting socket");

				// Minimal delay after refresh - just enough for cookies to be set
				setTimeout(() => {
					this.connectSocket(socketUrl);
				}, 100); // Reduced from 500ms to 100ms
			} else {
				console.error("Auth refresh failed, cannot connect socket");
				this.isInitialized = false;
			}
		} catch (error) {
			console.error("Error in auth process:", error);
			this.isInitialized = false;
		}
	}

	// Separate method to connect socket with proper configuration
	private connectSocket(socketUrl: string) {
		console.log("Connecting socket with proper auth configuration");

		const url = socketUrl || undefined;

		// Connect with optimized settings for auth cookies
		this.socket = io(url, {
			path: "/socket.io/",
			// Start with polling so proxyed environments can establish the session
			// and then upgrade to websocket when available.
			transports: ["polling", "websocket"],
			reconnection: true,
			reconnectionDelay: 2000,
			reconnectionAttempts: 3,
			timeout: 20000,
			withCredentials: true, // Critical: ensures cookies are sent
		});

		// Log a warning if cookies are not accessible (HTTP-only)
		if (!document.cookie.includes("io=")) {
			console.log("Warning: Socket.IO 'io' cookie not visible - this is normal if it's HTTP-only");
		}

		this.setupConnectionHandlers();
		this.setupEventListeners();
	}

	private setupConnectionHandlers() {
		if (!this.socket) return;

		this.socket.on("connect", () => {
			console.log("Socket connected with ID:", this.socket?.id);
			// Reset reconnection attempts on successful connection
			this.reconnectAttempts = 0;
		});

		this.socket.on("connect_error", (error) => {
			console.error("Socket connection error:", error);

			// Most connect_error events are due to auth issues
			if (
				error.message.includes("Authentication") ||
				error.message.includes("Invalid token") ||
				error.message.includes("401")
			) {
				console.log("Socket connection auth error detected");
				this.authError = true;

				// Immediately try to refresh auth
				this.handleAuthError();
			} else {
				// Handle other non-auth connection errors
				console.log("Non-auth socket connection error:", error.message);

				// Implement basic retry with backoff
				if (this.reconnectAttempts < this.maxReconnectAttempts) {
					this.reconnectAttempts++;
					setTimeout(() => {
						if (this.socket) {
							console.log(`Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);
							this.socket.connect();
						}
					}, 1000 * this.reconnectAttempts);
				}
			}
		});

		this.socket.on("disconnect", (reason) => {
			console.log("Socket disconnected:", reason);

			// If server disconnected us due to auth, try to refresh token
			if (reason === "io server disconnect") {
				this.authError = true;
				this.handleAuthError();
			}
		});

		this.socket.on("error", (error) => {
			console.error("Socket error:", error);

			// Auth errors during an established connection
			if (
				error.message &&
				(error.message.includes("Authentication") || error.message.includes("Invalid token"))
			) {
				this.authError = true;
				this.handleAuthError();
			}
		});
	}

	private async handleAuthError() {
		// If we're already handling an auth error, don't try again
		if (this.authError) {
			console.log("Auth error already being handled");
			return;
		}

		// Set flag to prevent multiple refreshes
		this.authError = true;
		console.log("Socket auth error detected, attempting to refresh token");

		try {
			// Completely disconnect the socket first to clean up state
			if (this.socket) {
				console.log("Disconnecting socket before attempting refresh");
				this.socket.disconnect();
				this.socket = null;
			}

			// Make a direct call to refresh the token which will set new cookies with proper fingerprinting
			console.log("Attempting API auth refresh");
			const refreshResponse = await fetch(`${config.VITE_API_BASE_URL}/auth/refresh`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
				},
			});

			if (refreshResponse.ok) {
				console.log("Auth refreshed successfully, reconnecting socket");

				// Reset auth error state immediately
				this.authError = false;

				// Re-initialize the socket with minimal delay
				if (!this.socket) {
					const socketUrl = config.VITE_SOCKET_URL;
					// Use minimal delay for reconnection
					setTimeout(() => {
						this.connectSocket(socketUrl);
					}, 50);
				}
				return;
			} else {
				console.warn(`API refresh failed with status: ${refreshResponse.status}`);

				// If refresh fails, reset the auth error state more quickly
				setTimeout(() => {
					this.authError = false;
				}, 1000);
			}
		} catch (error) {
			console.error("Auth refresh error:", error);

			// Reset auth error flag more quickly
			setTimeout(() => {
				this.authError = false;
			}, 1000);
		}
	}

	private setupEventListeners() {
		if (!this.socket) return;

		this.socket.removeAllListeners("departureUpdate");
		this.socket.removeAllListeners("departureAssignmentUpdate");

		const handleUpdate = (data: DepartureAssignmentUpdate) => {
			if (!data || typeof data.departureId !== "number") return;

			// Ensure arrays are preserved in the update
			if (data.departure) {
				data.departure = {
					...data.departure,
					groupLeaders: data.departure.groupLeaders || [],
					programmeAssistants: data.departure.programmeAssistants || [],
				};
			}

			this.departureUpdateHandlers.forEach((handler) =>
				handler({
					...data,
					type: data.type || "UPDATE",
				})
			);
		};

		this.socket.on("departureAssignmentUpdate", handleUpdate);
		this.socket.on("departureUpdate", handleUpdate);
	}

	onDepartureUpdate(handler: DepartureUpdateHandler) {
		if (!this.isInitialized) {
			this.initialize();
		}
		this.departureUpdateHandlers.add(handler);
		return () => {
			this.departureUpdateHandlers.delete(handler);
			if (this.departureUpdateHandlers.size === 0) {
				this.disconnect();
			}
		};
	}

	onDeparturesRefreshNeeded(handler: (data: { type: "regular" | "road-scholar" }) => void) {
		if (!this.isInitialized) {
			this.initialize();
		}
		this.socket?.on("departuresRefreshNeeded", handler);
		return () => {
			this.socket?.off("departuresRefreshNeeded", handler);
		};
	}

	disconnect() {
		if (this.socket) {
			this.socket.disconnect();
			this.socket = null;
		}
		this.isInitialized = false;
		this.reconnectAttempts = 0;
	}

	isConnected(): boolean {
		return this.socket?.connected ?? false;
	}

	resetAuthError(): void {
		this.authError = false;
	}
}

export const socketService = SocketService.getInstance();
